import { type ReactElement } from "react";

import { ButtonLink, Text } from "@backstage/ui";

import { useIsActiveRoute } from "../hooks";
import styles from "./SidebarItem.module.css";
import { useSidebarOpen } from "./sidebarOpenState";

/**
 * A single navigable row in the nav rail.
 *
 * Replaces `SidebarItem` from `@backstage/core-components`. Built on BUI's
 * `ButtonLink`, which is the primitive that matches a nav row: button
 * *appearance* with link *semantics*. It renders a real anchor, so the row
 * keeps its `link` role, stays in the browser's links list, and is reachable
 * with Tab like any other link.
 *
 * `ButtonLink` renders a React Aria `Link`, and `plugin-app` mounts a
 * `BUIProvider` — and therefore React Aria's `RouterProvider` — above the
 * layout. That supplies both `navigate` and `useHref`, so the row resolves its
 * target through React Router and navigates client-side instead of reloading.
 *
 * Choosing it over BUI's plain `Link` also removes a pile of hand-written CSS:
 * `size="medium"` already supplies the 40px row height and horizontal padding,
 * and `variant="tertiary"` supplies the hover and pressed fills as *translucent
 * overlays*, which sit correctly on whatever the rail's background happens to
 * be. The hardcoded fills this replaced had to be re-derived by hand every time
 * that background changed.
 *
 * Collapsed, the visible label is dropped so it cannot overflow the 72px rail,
 * and `aria-label` carries the name instead. There is deliberately no tooltip:
 * the rail expands on hover or focus before an item can be pointed at or tabbed
 * to, so a collapsed-only tooltip would never be shown. `aria-label` still
 * matters, because assistive technology can read the collapsed rail with a
 * virtual cursor without moving focus, and the icon is `aria-hidden`.
 */
export const SidebarItem = (props: {
    icon: ReactElement;
    text: string;
    href: string;
}) => {
    const { icon, text, href } = props;
    const isOpen = useSidebarOpen();
    const isActive = useIsActiveRoute(href);

    return (
        <ButtonLink
            href={href}
            size="medium"
            variant="tertiary"
            iconStart={icon}
            aria-label={text}
            aria-current={isActive ? "page" : undefined}
            data-active={isActive}
            data-open={isOpen}
            className={styles.root}
        >
            {isOpen ? (
                <Text variant="body-medium" className={styles.text}>
                    {text}
                </Text>
            ) : undefined}
        </ButtonLink>
    );
};
